import { cn } from '@portals-platform/ui';
import type * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { CONTROL_ERROR, CONTROL_INTERACTION } from '../lib/control-classes.js';
import { useFieldA11y } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';
import { useRegistration } from '../lib/use-registration.js';

interface TextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  maxLength?: number;
  showCount?: boolean;
}

export function TextareaField({
  name,
  maxLength,
  showCount,
  className,
  onChange,
  onBlur,
  ...props
}: TextareaFieldProps) {
  const { watch } = useFormContext();
  const hasError = !!useFieldError(name);
  const a11y = useFieldA11y(name);
  const bind = useRegistration<HTMLTextAreaElement>(name, { onChange, onBlur });
  const value = watch(name) as string | undefined;
  const length = (value ?? '').length;

  const countId = `${a11y.id}-count`;
  const showCounter = showCount || maxLength !== undefined;

  return (
    <div className="flex flex-col gap-1">
      <textarea
        {...a11y}
        // The live character count describes the control too, so append it rather than
        // letting it overwrite the hint/error wiring.
        aria-describedby={
          showCounter
            ? [a11y['aria-describedby'], countId].filter(Boolean).join(' ')
            : a11y['aria-describedby']
        }
        className={cn(
          'min-h-24 w-full resize-y rounded-[var(--radius-md)] border border-input bg-muted/40 px-3 py-2.5',
          'text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground/60 outline-none',
          'transition-[border-color,background,box-shadow] duration-150',
          CONTROL_INTERACTION,
          hasError && CONTROL_ERROR,
          className,
        )}
        maxLength={maxLength}
        {...props}
        {...bind}
      />
      {showCounter && (
        <p
          id={countId}
          // Announce the count as it changes, but politely — typing should not interrupt.
          aria-live="polite"
          className="text-right text-[11px] text-muted-foreground"
        >
          {length}
          {maxLength ? ` / ${maxLength}` : ''}
          <span className="sr-only"> characters{maxLength ? ` of ${maxLength} allowed` : ''}</span>
        </p>
      )}
    </div>
  );
}
